/**
 * Voice Chat Integration Service
 *
 * This service connects the chat functionality with the voice service
 * It handles creating, tracking, and closing voice conversations that are
 * associated with chat conversations.
 */

import { API_URL } from "../../../common/constants";
import protectedAxios from "../../../common/axios";

/**
 * Creates or updates a voice conversation (upsert pattern)
 * If an active conversation exists, it will be updated, otherwise a new one is created
 *
 * This uses a single POST endpoint that implements upsert logic on the server side:
 * 1. First tries to find an active conversation by conversationId
 * 2. If found, updates that conversation with new data
 * 3. If not found, creates a new conversation
 *
 * This simplifies the client-side logic by handling all cases on the server.
 *
 * @param {string} conversationId - The ID of the chat conversation
 * @param {string} callId - The call ID from VAPI service
 * @param {Object} metadata - Additional metadata for the voice conversation
 * @returns {Promise<Object>} - The voice conversation
 */
export const upsertVoiceConversation = async (
  conversationId,
  callId,
  metadata = {}
) => {
  try {
    // NOTE: userId is automatically determined from the JWT token on the server side
    // We don't need to include it in the payload
    const payload = {
      conversationId,
      callId: callId || "NA",
      metadata: {
        ...metadata,
        lastActivity: Date.now(),
      },
    };

    // Single endpoint for creating or updating an active voice conversation
    const response = await protectedAxios.post(
      `${API_URL}/v1/ai/voice`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error("Error upserting voice conversation:", error);
    throw error;
  }
};

/**
 * Ends a voice conversation when a chat ends
 *
 * @param {string} conversationId - The ID of the chat conversation
 * @returns {Promise<Object>} - Updated voice conversation
 */
export const closeVoiceConversation = async (conversationId) => {
  try {
    const response = await protectedAxios.patch(
      `${API_URL}/v1/ai/voice/conversation/${conversationId}/close`
    );

    return response.data;
  } catch (error) {
    console.error("Error closing voice conversation:", error);
    throw error;
  }
};

/**
 * Handles starting a voice call for a chat conversation
 * Creates or updates a voice conversation record
 *
 * @param {string} conversationId - The chat conversation ID
 * @param {string} callId - The voice call ID from VAPI
 * @param {Object} metadata - Additional metadata for the voice conversation
 * @returns {Promise<Object>} - The voice conversation record
 */
export const startVoiceCallForChat = async (
  conversationId,
  callId,
  metadata = {}
) => {
  return await upsertVoiceConversation(conversationId, callId, metadata);
};

/**
 * Updates the details of an active voice call
 *
 * @param {string} conversationId - The ID of the chat conversation
 * @param {Object} callDetails - Updated details for the call
 * @returns {Promise<Object>} - The updated voice conversation
 */
export const updateVoiceCallDetails = async (conversationId, callDetails) => {
  try {
    const response = await protectedAxios.patch(
      `${API_URL}/v1/ai/voice/conversation/${conversationId}`,
      callDetails
    );

    return response.data;
  } catch (error) {
    console.error("Error updating voice call details:", error);
    throw error;
  }
};

/**
 * Handles ending a voice call for a chat conversation
 *
 * @param {string} conversationId - The chat conversation ID
 * @returns {Promise<Object>} - The updated voice conversation
 */
export const endVoiceCallForChat = async (conversationId) => {
  return await closeVoiceConversation(conversationId);
};
